/**
 * Anchors, NOW and stored meaning (1.K-1 deliverable 13): Base §6's reference-resolution rules as
 * pure helpers, which 1.L-2 applies in its write transaction and 1.O-1's placement picker shows.
 * None of them throws.
 */
import { isTick } from '@codex/contracts';
import { add } from '../engine/add.js';
import { toParts } from '../engine/from-tick.js';
import { isEmptyParts, isPartsObject } from '../engine/ticks.js';
import { isCompiled } from '../engine/types.js';
/**
 * The anchor's edge a relation reads: `after` its end, `before` its start, `during` its whole span
 * (both edges of the new placement inside it), `same_time` its start. Null for anything else.
 */
const EDGES = { after: 'end', before: 'start', during: 'span', same_time: 'start' };
export function anchorEdge(relation) {
    return typeof relation === 'string' && Object.hasOwn(EDGES, relation) ? EDGES[relation] : null;
}
/** Code-unit order with nulls last; no locale (rule 9). */
function compareNullable(a, b) {
    if (a === b)
        return 0;
    if (a === null)
        return 1;
    if (b === null)
        return -1;
    return a < b ? -1 : 1;
}
function isPlacement(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const p = value;
    return (typeof p.id === 'string' &&
        typeof p.role === 'string' &&
        typeof p.effectiveStatus === 'string' &&
        (p.label === null || typeof p.label === 'string') &&
        (p.lo === null || isTick(p.lo)));
}
/**
 * Exactly one `occurs` placement with effective status `canon` is used; none creates an undated
 * `occurs` placement with origin `auto` (even beside a planned or rumored one, orchestrator
 * ruling 15); several let the GM pick among every placement of the document, ordered by role,
 * label, then `lo` with undated ones last (then by id, so the order is total).
 */
export function resolveAnchor(placements) {
    const all = Array.isArray(placements) ? placements.filter(isPlacement) : [];
    const canon = all.filter((p) => p.role === 'occurs' && p.effectiveStatus === 'canon');
    if (canon.length === 1)
        return { kind: 'use', id: canon[0].id };
    if (canon.length === 0)
        return { kind: 'create_auto' };
    const options = all
        .map((p) => ({
        id: p.id,
        role: p.role,
        effectiveStatus: p.effectiveStatus,
        label: p.label,
        lo: p.lo,
    }))
        .sort((a, b) => compareNullable(a.role, b.role) ||
        compareNullable(a.label, b.label) ||
        compareNullable(a.lo, b.lo) ||
        compareNullable(a.id, b.id));
    return { kind: 'pick', options };
}
/**
 * What the server stores. A `now` phrase becomes a minute-precision point with explicit parts (so
 * a stored date never depends on the clock later); a point's every non-empty side without an era
 * gets the default era's key (so an era added later cannot change what it means); other kinds pass
 * through. A `now` without a NOW is `now_unset`; a NOW (with its offset) that leaves the tick range
 * or every era is `time_out_of_range`.
 */
export function materialize(parsed, calendar, options) {
    if (typeof parsed !== 'object' || parsed === null) {
        return { kind: 'invalid', reason: 'unexpected_text', at: 0 };
    }
    if (parsed.kind !== 'point' && parsed.kind !== 'now')
        return parsed;
    if (!isCompiled(calendar))
        return { kind: 'invalid', reason: 'calendar_invalid', at: 0 };
    if (parsed.kind === 'now') {
        const now = options?.now;
        if (!isTick(now))
            return { kind: 'invalid', reason: 'now_unset', at: 0 };
        const offset = parsed.offset;
        if (offset !== null && (typeof offset !== 'object' || offset === undefined)) {
            return { kind: 'invalid', reason: 'unexpected_text', at: 0 };
        }
        const moved = offset === null
            ? { ok: true, tick: now }
            : add(calendar, now, offset.n, offset.unit);
        const parts = moved.ok ? toParts(calendar, moved.tick, 'minute') : null;
        if (parts === null)
            return { kind: 'invalid', reason: 'time_out_of_range', at: 0 };
        return {
            kind: 'point',
            loParts: parts,
            hiParts: null,
            precision: 'minute',
            qualifier: 'exact',
        };
    }
    const era = calendar.defaultEra.key;
    const withEra = (side) => {
        if (!isPartsObject(side))
            return side;
        if (isEmptyParts(side) || side.era !== undefined)
            return { ...side };
        return { era, ...side };
    };
    return {
        ...parsed,
        loParts: withEra(parsed.loParts),
        hiParts: withEra(parsed.hiParts),
    };
}
//# sourceMappingURL=resolve.js.map